import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '~/components/isolated/Button';
import MainSidebar from '~/components/MainSidebar/MainSidebar';
import { useGlobal } from '~/hooks/useGlobal';
import { InfinityScrollLoader } from '~/pages/Catalog/InfinityScrollLoader';
import { cartStore } from '~/stores';
import { PromocodesTab } from '~/stores/PromotionsStore';

import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../Main/MobileHeader/MobileHeaderTitle';

import promocodesBannerDesktop from './assets/images/promocodes-banner-desktop.png';
import promocodesBannerMobile from './assets/images/promocodes-banner-mobile.png';
import promocodesNotFound from './assets/images/promocodes-not-found.png';
import PromocodeCard from './components/PromocodeCard';
import { PromocodesCardsSkeleton } from './components/PromocodesSkeleton';

const Promocodes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useGlobal();

  const {
    promocodes,
    isPromocodesLoading,
    promocodesTab,
    setPromocodesTab,
    canLoadMorePromocodes,
    fetchNextPage,
  } = cartStore.promotionStore;

  const { promocodesTab: promocodeTabParam = PromocodesTab.AVAILABLE } =
    useParams<{
      promocodesTab?: PromocodesTab;
    }>();

  const TABS = [
    {
      alias: PromocodesTab.AVAILABLE,
      title: t('availablePromocodes'),
    },
    {
      alias: PromocodesTab.REFERRAL,
      title: t('referralPromocodes'),
    },
    {
      alias: PromocodesTab.HISTORY,
      title: t('historyPromocodes'),
    },
  ];

  useEffect(() => {
    if (!TABS.some(({ alias }) => alias === promocodeTabParam)) {
      navigate(`/cabinet/promocodes/${PromocodesTab.AVAILABLE}`, {
        replace: true,
      });
      return;
    }
    if (promocodeTabParam !== promocodesTab) {
      setPromocodesTab(promocodeTabParam);
    }
  }, [promocodeTabParam, promocodesTab, setPromocodesTab, navigate]);

  return (
    <>
      <MobileHeaderLayout
        content={<MobileHeaderTitle text={t('promocodes')} />}
      />

      <div className="content promocodes">
        <aside className="sidebar">
          <MainSidebar />
        </aside>

        <main className="container">
          <h1 className="page-title">{t('promocodes')}</h1>
          <section className="banner">
            <img
              src={isMobile ? promocodesBannerMobile : promocodesBannerDesktop}
              alt="Promocode Banner"
            />
            <div className="banner-overlay">
              <div className="banner-content">
                <h2 className="banner-title">{t('promocodesBannerTitle')}</h2>
                <p className="banner-description">
                  {t('promocodesBannerDescription')}
                </p>
                <Link to="/cabinet/referral">
                  <Button
                    variant="shadowS"
                    size={isMobile ? 'XS' : 'XL'}
                    className="banner-button"
                  >
                    {t('promocodesBannerButton')}
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          <section className="navigation">
            <div className="tabs">
              {TABS.map(({ alias, title }) => (
                <Link
                  key={alias}
                  to={`/cabinet/promocodes/${alias}`}
                  replace
                  className={classes([
                    'tab',
                    promocodesTab === alias && 'current',
                  ])}
                  onClick={() => setPromocodesTab(alias)}
                >
                  {title}
                </Link>
              ))}
            </div>
          </section>
          <section className="tab-content">
            {isPromocodesLoading ? (
              <PromocodesCardsSkeleton />
            ) : promocodes.length > 0 ? (
              <>
                {promocodes.map((promocode) => (
                  <PromocodeCard key={promocode.id} promocode={promocode} />
                ))}
                {canLoadMorePromocodes && (
                  <InfinityScrollLoader
                    embedded
                    onLoadMore={() => fetchNextPage()}
                    skeletons={<PromocodesCardsSkeleton />}
                  />
                )}
              </>
            ) : (
              <div className="promocodes-not-found">
                <img src={promocodesNotFound} alt="Promocodes Not Found" />
                <h3>{t(`promocodesNotFoundTitle.${promocodesTab}`)}</h3>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
};

export default observer(Promocodes);
